// View/Walk/Layer/Tool toggle controls

import { t } from '@/i18n'
import { debug } from '@/utils/debug'
import {
  viewToggle,
  walkToggle,
  walkRow,
  controlsHint,
  bottomBarHintText,
  layerPlantsBtn,
  layerBuildingsBtn,
  layerGroundBtn,
  layerMetadataBtn,
  toolHandBtn,
  toolPointerBtn,
  topBarPanBtn,
  topBarSelectBtn,
  topBarRulerBtn,
  topBarLayerPlantsBtn,
  topBarLayerBuildingsBtn,
  topBarLayerGroundBtn,
  topBarLayerMetadataBtn,
  topBarLayerMapBtn,
} from './elements'
import { getViewer, getToolMode, setToolMode, getPlacementMode, stopContinuousPlacement, type ToolMode } from './state'

// Grab cursor listeners for hand/pan mode
let grabMouseDown: ((e: MouseEvent) => void) | null = null
let grabMouseUp: ((e: MouseEvent) => void) | null = null

/**
 * Update view toggle button state and hints
 */
export function updateViewToggle(): void {
  const viewer = getViewer()
  if (!viewer) return

  const mode = viewer.getViewMode()
  const isWalking = viewer.isWalkMode()

  let hintText: string
  if (mode === '3d') {
    viewToggle.classList.add('active')
    // Show walk button only in 3D mode
    walkRow.style.display = 'flex'

    if (isWalking) {
      hintText = t('controls.walk')
    } else {
      hintText = t('controls.3d')
    }
  } else {
    viewToggle.classList.remove('active')
    // Hide walk button in 2D mode
    walkRow.style.display = 'none'
    hintText = t('controls.2d')
  }

  // Update old UI hint (always shows camera controls)
  controlsHint.textContent = hintText

  // Bottom bar: show tool hint when a non-hand tool is active, otherwise camera controls
  updateBottomBarHint()
}

/**
 * Update bottom bar hint based on current tool mode and active tool.
 * Hand mode → camera controls; pointer/ruler → active tool's hint key.
 */
export function updateBottomBarHint(): void {
  const viewer = getViewer()
  const mode = getToolMode()

  // In hand mode, show camera control hints
  if (mode === 'hand' || !viewer) {
    const viewMode = viewer?.getViewMode() ?? '2d'
    const isWalking = viewer?.isWalkMode() ?? false
    if (viewMode === '3d' && isWalking) {
      bottomBarHintText.textContent = t('controls.walk')
    } else if (viewMode === '3d') {
      bottomBarHintText.textContent = t('controls.3d')
    } else {
      bottomBarHintText.textContent = t('controls.2d')
    }
    return
  }

  // In pointer/ruler mode, show active tool's hint
  const activeTool = viewer.getToolManager()?.getActiveTool()
  if (activeTool?.hint) {
    bottomBarHintText.textContent = t(activeTool.hint)
  }
}

/**
 * Update walk toggle button state
 */
export function updateWalkToggle(): void {
  const viewer = getViewer()
  if (!viewer) return

  const isWalking = viewer.isWalkMode()

  if (isWalking) {
    walkToggle.classList.add('active')
  } else {
    walkToggle.classList.remove('active')
  }

  updateViewToggle() // Update hints
}

/**
 * Toggle between 2D and 3D view modes
 */
export function toggleViewMode(): void {
  const viewer = getViewer()
  if (!viewer) return

  const currentMode = viewer.getViewMode()
  const newMode = currentMode === '2d' ? '3d' : '2d'
  viewer.setViewMode(newMode)
  updateViewToggle()
  updateWalkToggle()
  debug.log('App', `View mode toggled to: ${newMode}`)
}

/**
 * Toggle walk mode (first-person view)
 */
export function toggleWalkMode(): void {
  const viewer = getViewer()
  if (!viewer) return

  // Can only toggle walk mode in 3D view
  if (viewer.getViewMode() !== '3d') return

  const newWalkMode = !viewer.isWalkMode()
  viewer.setWalkMode(newWalkMode)
  updateWalkToggle()
  debug.log('App', `Walk mode toggled to: ${newWalkMode}`)
}

/**
 * Toggle visibility of a layer
 */
export function toggleLayerVisibility(layer: 'plants' | 'buildings' | 'ground' | 'metadata' | 'map'): void {
  const viewer = getViewer()
  if (!viewer) return

  const newState = viewer.toggleLayer(layer)
  updateLayerButtons()
  debug.log('App', `Layer '${layer}' toggled to: ${newState}`)
}

/**
 * Update layer button active states
 */
export function updateLayerButtons(): void {
  const viewer = getViewer()
  if (!viewer) return

  const visibility = viewer.getLayerVisibility()

  // Update old UI button active state based on layer visibility
  layerPlantsBtn.classList.toggle('active', visibility.plants)
  layerBuildingsBtn.classList.toggle('active', visibility.buildings)
  layerGroundBtn.classList.toggle('active', visibility.ground)
  layerMetadataBtn.classList.toggle('active', visibility.metadata)

  // Update top bar layer buttons (order: 1-5 matches keyboard shortcuts)
  topBarLayerPlantsBtn.classList.toggle('active', visibility.plants)
  topBarLayerBuildingsBtn.classList.toggle('active', visibility.buildings)
  topBarLayerGroundBtn.classList.toggle('active', visibility.ground)
  topBarLayerMetadataBtn.classList.toggle('active', visibility.metadata)
  topBarLayerMapBtn.classList.toggle('active', visibility.map)
}

/**
 * Set tool mode and update UI
 */
export function selectToolMode(mode: ToolMode): void {
  const viewer = getViewer()

  setToolMode(mode)
  updateToolButtons()

  // Stop continuous placement when switching to hand (view) mode
  if (mode === 'hand' && getPlacementMode() === 'continuous') {
    stopContinuousPlacement()
    viewer?.getToolManager()?.setActiveTool('select')
  }

  // Activate ruler tool when switching to ruler mode
  if (mode === 'ruler') {
    viewer?.getToolManager()?.setActiveTool('ruler')
  } else if (mode === 'pointer') {
    // Ensure we're on select tool when switching back to pointer
    const tm = viewer?.getToolManager()
    if (tm && tm.getActiveToolName() === 'ruler') {
      tm.setActiveTool('select')
    }
  }

  // Enable/disable selection based on mode
  const selectionManager = viewer?.getScene()?.getSelectionManager()
  if (selectionManager) {
    const enabled = mode === 'pointer'
    selectionManager.setEnabled(enabled)

    // Clear selection and hover when switching away from pointer
    if (!enabled) {
      selectionManager.deselect()
      selectionManager.clearHover()
      viewer?.getScene()?.getHighlightManager()?.clearHover()
    }
  }

  // Set grab cursor on canvas for hand mode
  const canvas = viewer?.getScene()?.getRendererDomElement()
  if (canvas) {
    // Remove previous grab listeners
    if (grabMouseDown) {
      canvas.removeEventListener('mousedown', grabMouseDown)
      grabMouseDown = null
    }
    if (grabMouseUp) {
      canvas.removeEventListener('mouseup', grabMouseUp)
      grabMouseUp = null
    }

    if (mode === 'hand') {
      canvas.style.cursor = 'grab'
      grabMouseDown = () => { canvas.style.cursor = 'grabbing' }
      grabMouseUp = () => { canvas.style.cursor = 'grab' }
      canvas.addEventListener('mousedown', grabMouseDown)
      canvas.addEventListener('mouseup', grabMouseUp)
    } else {
      canvas.style.cursor = ''
    }
  }

  updateBottomBarHint()
  debug.log('App', `Tool mode: ${mode}`)
}

/**
 * Update tool button active states (both old UI and top bar)
 */
export function updateToolButtons(): void {
  const mode = getToolMode()

  // Old UI buttons
  toolHandBtn.classList.toggle('active', mode === 'hand')
  toolPointerBtn.classList.toggle('active', mode === 'pointer')

  // Top bar buttons
  topBarPanBtn.classList.toggle('active', mode === 'hand')
  topBarSelectBtn.classList.toggle('active', mode === 'pointer')
  topBarRulerBtn.classList.toggle('active', mode === 'ruler')
}

